import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowLeft, Edit, Trash2, X, Save, Image as ImageIcon, Stethoscope } from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";
import NotesPanel from "../components/NotesPanel";
import AttachmentsPanel from "../components/AttachmentsPanel";
import VetVisits from "../components/VetVisits";
import { AlertTriangle } from "lucide-react";

const RESTRICTION_LABEL = {
  box_rest: "Box rest",
  walker_only: "Walker only",
  no_jumping: "No jumping",
  no_paddock: "No paddock",
  light_work_only: "Light work only",
  other: "Restriction",
};
const fmtShortDate = (iso) => { try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { day: "numeric", month: "short" }); } catch { return iso; } };
import CareTracker from "../components/CareTracker";
import VaccinationTracker from "../components/VaccinationTracker";
import PhotoUpload from "../components/PhotoUpload";
import { usePermissions, canOwnerWrite } from "../lib/permissions";
import { resolveImage } from "../lib/images";
import { useAuth } from "../context/AuthContext";
import { Wallet } from "lucide-react";

const COST_VISIBLE_ROLES = new Set(["super_admin", "admin", "manager", "vet"]);

const CARE_TYPE_LABEL = {
  shoeing: "Shoeing",
  deworming: "Deworming",
  dental: "Dental",
};

function HealthCostSummary({ horseId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    api.get(`/horses/${horseId}/cost-summary`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(formatApiError(e)); });
    return () => { cancelled = true; };
  }, [horseId]);

  if (err) return null;

  const fmt = (n) => `${data?.currency === "EUR" ? "€" : ""}${Number(n || 0).toFixed(2)}`;
  const byCare = data?.by_care_type || {};
  const careKeys = Object.keys(byCare);
  const empty = !data || (careKeys.length === 0 && !(data.vaccination_total > 0));

  return (
    <div className="se-card" data-testid="health-cost-summary">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div>
          <div className="se-label flex items-center gap-2"><Wallet size={12} /> Financials</div>
          <h3 className="font-heading mt-1" style={{ fontSize: 22 }}>Health cost summary</h3>
        </div>
        <div className="text-right">
          <div className="se-label">Total spend</div>
          <div className="font-heading" style={{ fontSize: 24, color: "#3B2F1E" }} data-testid="cost-summary-grand-total">
            {fmt(data?.grand_total)}
          </div>
        </div>
      </div>
      {empty ? (
        <p className="text-sm" style={{ color: "#8C9E8A" }} data-testid="cost-summary-empty">
          No cost data yet. Add a cost when logging shoeing, deworming, dental or vaccination events.
        </p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {careKeys.map((ct) => (
            <div
              key={ct}
              className="p-3 rounded-lg"
              style={{ background: "rgba(212,169,106,0.08)", border: "0.5px solid rgba(212,169,106,0.25)" }}
              data-testid={`cost-summary-care-${ct}`}
            >
              <div className="se-label">{CARE_TYPE_LABEL[ct] || ct}</div>
              <div className="font-heading mt-1" style={{ fontSize: 18 }}>{fmt(byCare[ct])}</div>
            </div>
          ))}
          {data?.vaccination_total > 0 && (
            <div
              className="p-3 rounded-lg"
              style={{ background: "rgba(45,110,110,0.08)", border: "0.5px solid rgba(45,110,110,0.25)" }}
              data-testid="cost-summary-vaccinations"
            >
              <div className="se-label">Vaccinations</div>
              <div className="font-heading mt-1" style={{ fontSize: 18 }}>{fmt(data.vaccination_total)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const EDITABLE_FIELDS = [
  { key: "name", label: "Name", required: true },
  { key: "nickname", label: "Nickname" },
  { key: "stall_number", label: "Stall #" },
  { key: "height_cm", label: "Height (cm)", type: "number" },
  { key: "weight_kg", label: "Weight (kg)", type: "number" },
  { key: "date_of_birth", label: "Date of birth (YYYY-MM-DD)" },
  { key: "passport_number", label: "Passport #" },
  { key: "microchip_number", label: "Microchip #" },
  { key: "notes", label: "Description", type: "textarea" },
  // ENH-018: Vet Contact fields (per-horse primary vet).
  { key: "vet_contact_name", label: "Vet name" },
  { key: "vet_contact_phone", label: "Vet phone" },
  { key: "vet_contact_notes", label: "Vet notes", type: "textarea" },
];

const GENDERS = [
  { value: "", label: "—" }, { value: "stallion", label: "Stallion" },
  { value: "mare", label: "Mare" }, { value: "gelding", label: "Gelding" },
];
const DISCIPLINES = [
  { value: "", label: "—" }, { value: "show_jumping", label: "Show Jumping" },
  { value: "dressage", label: "Dressage" }, { value: "other", label: "Other" },
];
const STATUSES = [
  { value: "active", label: "Active" }, { value: "injured", label: "Injured" },
  { value: "retired", label: "Retired" }, { value: "sold", label: "Sold" },
];

// ENH-009: shared with the backend HORSE_CALENDAR_PALETTE — used by the picker
// swatches on the admin/manager/vet horse edit form and OwnerEditHorseModal.
const CALENDAR_PALETTE = [
  "#E74C3C","#E67E22","#F1C40F","#2ECC71","#1ABC9C",
  "#3498DB","#9B59B6","#E91E63","#00BCD4","#8BC34A",
  "#FF5722","#607D8B","#795548","#F06292","#AED581",
  "#4DD0E1","#7986CB","#A1887F","#90A4AE","#FFB74D",
];

export default function HorseProfile() {
  const { horseId } = useParams();
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermissions();
  const { user } = useAuth();
  const showCostSummary = COST_VISIBLE_ROLES.has(user?.role);

  const [horse, setHorse] = useState(null);
  const [owners, setOwners] = useState([]);
  const [activeRestrictions, setActiveRestrictions] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const [toast, setToast] = useState("");
  const [ownerEditing, setOwnerEditing] = useState(false);

  const refetchRestrictions = () => {
    api.get(`/horses/${horseId}/vet-visits/active-restrictions`)
      .then((r) => setActiveRestrictions(r.data))
      .catch(() => {});
  };

  const load = async () => {
    try {
      const [h, o] = await Promise.all([
        api.get(`/horses/${horseId}`),
        api.get("/owners"),
      ]);
      setHorse(h.data);
      setOwners(o.data);
      // Best-effort — role=driver gets 403 which we silently swallow
      api.get(`/horses/${horseId}/vet-visits/active-restrictions`)
        .then((r) => setActiveRestrictions(r.data))
        .catch(() => setActiveRestrictions([]));
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, [horseId]);

  const startEdit = () => {
    setForm({
      name: horse.name || "",
      nickname: horse.nickname || "",
      stall_number: horse.stall_number || "",
      height_cm: horse.height_cm ?? "",
      weight_kg: horse.weight_kg ?? "",
      date_of_birth: horse.date_of_birth || "",
      passport_number: horse.passport_number || "",
      microchip_number: horse.microchip_number || "",
      photo_url: horse.photo_url || null,
      notes: horse.notes || "",
      gender: horse.gender || "",
      discipline: horse.discipline || "",
      status: horse.status || "active",
      owner_id: horse.owner_id || "",
      // ENH-018
      vet_contact_name: horse.vet_contact_name || "",
      vet_contact_phone: horse.vet_contact_phone || "",
      vet_contact_notes: horse.vet_contact_notes || "",
      // ENH-009
      calendar_color: horse.calendar_color || "",
    });
    setSaveError("");
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.owner_id) {
      setSaveError("Please assign an owner before saving.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      // Send every explicit form field, remapping empty strings to `null` so the
      // backend clears them. Required-FK fields (name, owner_id) are already
      // validated non-empty above.
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      await api.patch(`/horses/${horseId}`, payload);
      setEditing(false);
      setToast("Changes saved");
      setTimeout(() => setToast(""), 2200);
      await load();
    } catch (err) {
      setSaveError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/horses/${horseId}`);
      navigate("/horses");
    } catch (e) {
      setError(formatApiError(e));
      setConfirmDel(false);
    }
  };

  return (
    <div data-testid="horse-profile-page">
      <button
        onClick={() => navigate("/horses")}
        className="se-btn se-btn-ghost mb-6"
        data-testid="horse-profile-back"
      >
        <ArrowLeft size={16} /> Back to horses
      </button>

      {error && (
        <div className="se-card mb-4" data-testid="horse-profile-error" style={{ color: "#922B21" }}>{error}</div>
      )}

      {toast && (
        <div
          className="mb-4 se-card flex items-center gap-2"
          style={{ background: "rgba(74,124,89,0.12)", color: "#2D5E3A", borderColor: "rgba(74,124,89,0.3)" }}
          data-testid="horse-profile-toast"
        >
          <Save size={14} /> {toast}
        </div>
      )}

      {horse && !editing && (
        <div className="se-card">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-start gap-4 flex-1">
              {resolveImage(horse.photo_url) ? (
                <img
                  src={resolveImage(horse.photo_url)}
                  alt=""
                  style={{ width: 140, height: 94, objectFit: "cover", borderRadius: 10 }}
                  data-testid="horse-photo"
                />
              ) : (
                <div
                  style={{
                    width: 140, height: 94, borderRadius: 10,
                    background: "rgba(212,169,106,0.12)",
                    border: "0.5px dashed rgba(212,169,106,0.4)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#8C9E8A",
                  }}
                  data-testid="horse-photo-placeholder"
                >
                  <ImageIcon size={24} />
                </div>
              )}
              <div className="flex-1">
                <div className="se-label">Horse profile</div>
                <h1 className="font-heading mt-1 inline-flex items-center gap-2" style={{ fontSize: 28 }} data-testid="horse-profile-name">
                  {horse.calendar_color && (
                    <span
                      style={{ width: 12, height: 12, borderRadius: 6, background: horse.calendar_color, display: "inline-block", flexShrink: 0 }}
                      data-testid="horse-profile-color-dot"
                      title="Calendar colour"
                    />
                  )}
                  {horse.name}
                </h1>
                <p className="text-sm mt-1" style={{ color: "#8C9E8A" }}>
                  {horse.nickname ? `“${horse.nickname}” · ` : ""}Stall {horse.stall_number || "—"} · Owner{" "}
                  <button
                    className="se-link"
                    onClick={() => horse.owner_id && navigate(`/owners/${horse.owner_id}`)}
                    data-testid="horse-profile-owner-link"
                  >
                    {horse.owner_name || "—"}
                  </button>
                </p>
                {activeRestrictions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="horse-restriction-banners">
                    {activeRestrictions.map((r, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
                        style={{ background: "rgba(192,57,43,0.1)", color: "#922B21", border: "0.5px solid rgba(192,57,43,0.3)" }}
                        data-testid={`horse-restriction-banner-${r.restriction_type}`}
                      >
                        <AlertTriangle size={12} />
                        <span style={{ fontWeight: 600 }}>{RESTRICTION_LABEL[r.restriction_type] || r.restriction_type}</span>
                        <span>until {fmtShortDate(r.end_date)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {canEdit && (
                <button className="se-btn" onClick={startEdit} data-testid="horse-edit-button">
                  <Edit size={14} /> Edit
                </button>
              )}
              {canOwnerWrite(user?.role) && (
                <button className="se-btn" onClick={() => setOwnerEditing(true)} data-testid="horse-owner-edit-button">
                  <Edit size={14} /> Edit my horse
                </button>
              )}
              {canDelete && (
                <button
                  className="se-btn"
                  style={{ background: "#C0392B" }}
                  onClick={() => setConfirmDel(true)}
                  data-testid="horse-delete-button"
                >
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
          </div>

          <div className="se-divider my-5" />

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <Field label="Nickname" value={horse.nickname} />
            <Field label="Gender" value={horse.gender} />
            <Field label="Discipline" value={horse.discipline} />
            <Field label="Height" value={horse.height_cm != null ? `${horse.height_cm} cm` : null} />
            <Field label="Weight" value={horse.weight_kg != null ? `${horse.weight_kg} kg` : null} />
            <Field label="Date of birth" value={horse.date_of_birth} />
            <Field label="Passport #" value={horse.passport_number} />
            <Field label="Microchip #" value={horse.microchip_number} />
            <Field label="Status" value={horse.status} />
          </div>

          {horse.notes && (
            <>
              <div className="se-divider my-5" />
              <div className="se-label mb-2">Description</div>
              <p className="text-sm whitespace-pre-wrap">{horse.notes}</p>
            </>
          )}
        </div>
      )}

      {horse && !editing && (
        <div className="mt-6 grid grid-cols-1 gap-6">
          <VetContactCard horse={horse} />
          <CareTracker horseId={horse.id} horseName={horse.name} />
          <VaccinationTracker horseId={horse.id} horseName={horse.name} />
          <VetVisits horseId={horse.id} horseName={horse.name} onRestrictionsChanged={refetchRestrictions} />
          {showCostSummary && <HealthCostSummary horseId={horse.id} />}
          <NotesPanel targetType="horse" targetId={horse.id} testid="horse-notes" />
          <AttachmentsPanel targetType="horse" targetId={horse.id} testid="horse-attachments" />
        </div>
      )}

      {horse && editing && (
        <form className="se-card" onSubmit={save} data-testid="horse-edit-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading" style={{ fontSize: 22 }}>Edit horse</h3>
            <button type="button" className="se-btn se-btn-ghost" onClick={() => setEditing(false)} data-testid="horse-edit-cancel">
              <X size={14} /> Cancel
            </button>
          </div>

          <div className="mb-5">
            <div className="se-label mb-2">Profile photo</div>
            <PhotoUpload
              value={form.photo_url}
              onChange={(url) => setForm({ ...form, photo_url: url })}
              purpose="horse"
              aspect="landscape"
              hint="Landscape · 600×400 recommended · max 2MB"
              testid="horse-photo-upload"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {EDITABLE_FIELDS.map((f) => (
              <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                <label className="se-label block mb-1">{f.label}{f.required ? " *" : ""}</label>
                {f.type === "textarea" ? (
                  <textarea
                    className="se-input"
                    style={{ height: 80, padding: "10px 14px" }}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    data-testid={`horse-edit-${f.key}`}
                  />
                ) : (
                  <input
                    className="se-input"
                    type={f.type === "number" ? "number" : "text"}
                    step={f.type === "number" ? "0.1" : undefined}
                    min={f.type === "number" ? 0 : undefined}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    required={!!f.required}
                    data-testid={`horse-edit-${f.key}`}
                  />
                )}
              </div>
            ))}
            <div>
              <label className="se-label block mb-1">Gender</label>
              <select className="se-input" value={form.gender || ""} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="horse-edit-gender">
                {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="se-label block mb-1">Discipline</label>
              <select className="se-input" value={form.discipline || ""} onChange={(e) => setForm({ ...form, discipline: e.target.value })} data-testid="horse-edit-discipline">
                {DISCIPLINES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label className="se-label block mb-1">Status</label>
              <select className="se-input" value={form.status || "active"} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="horse-edit-status">
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="se-label block mb-1">Owner <span style={{ color: "#C0392B" }}>*</span></label>
              <select
                className="se-input"
                value={form.owner_id || ""}
                onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                data-testid="horse-edit-owner"
              >
                <option value="">— Select an owner —</option>
                {owners.map((o) => <option key={o.id} value={o.id}>{o.full_name}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="se-label block mb-1">Calendar colour</label>
              <ColorSwatchPicker
                value={form.calendar_color}
                onChange={(c) => setForm({ ...form, calendar_color: c })}
                testidPrefix="horse-edit-color"
              />
            </div>
          </div>

          {saveError && (
            <div className="mt-4 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="horse-edit-error">
              {saveError}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-5">
            <button type="button" className="se-btn se-btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
            <button type="submit" className="se-btn" disabled={saving} data-testid="horse-edit-submit">
              {saving ? "Saving…" : (<><Save size={14} /> Save changes</>)}
            </button>
          </div>
        </form>
      )}

      <ConfirmDialog
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={doDelete}
        title={`Delete ${horse?.name}?`}
        description="This horse will be moved to trash. A super-admin can restore it later. All history stays in the audit log."
        confirmLabel="Yes, delete"
        testid="horse-delete-confirm"
      />

      {ownerEditing && (
        <OwnerEditHorseModal
          horse={horse}
          onClose={() => setOwnerEditing(false)}
          onSaved={(updated) => {
            setHorse(updated);
            setOwnerEditing(false);
            setToast("Horse updated");
            setTimeout(() => setToast(""), 2500);
          }}
        />
      )}
    </div>
  );
}

function OwnerEditHorseModal({ horse, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: horse?.name || "",
    nickname: horse?.nickname || "",
    date_of_birth: horse?.date_of_birth || "",
    discipline: horse?.discipline || "",
    passport_number: horse?.passport_number || "",
    microchip_number: horse?.microchip_number || "",
    notes: horse?.notes || "",
    photo_url: horse?.photo_url || "",
    // ENH-018
    vet_contact_name: horse?.vet_contact_name || "",
    vet_contact_phone: horse?.vet_contact_phone || "",
    vet_contact_notes: horse?.vet_contact_notes || "",
    // ENH-009
    calendar_color: horse?.calendar_color || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    // Client-side validation: name is required (backend enforces min_length=1).
    if (!form.name.trim()) {
      setErr("Some fields could not be updated.");
      return;
    }
    setBusy(true);
    try {
      // Send only fields the owner actually changed. Preserve empty strings for
      // clearable text fields so the backend can null them explicitly.
      const payload = {};
      const allowed = [
        "name", "nickname", "date_of_birth", "discipline",
        "passport_number", "microchip_number", "notes", "photo_url",
        // ENH-018
        "vet_contact_name", "vet_contact_phone", "vet_contact_notes",
        // ENH-009
        "calendar_color",
      ];
      allowed.forEach((k) => {
        const v = (form[k] ?? "");
        const orig = horse?.[k] ?? "";
        if (v !== orig) payload[k] = (v === "" && k !== "name") ? null : v;
      });
      if (Object.keys(payload).length === 0) { setErr("Nothing changed."); setBusy(false); return; }
      const { data } = await api.patch(`/horses/${horse.id}/owner-update`, payload);
      onSaved(data);
    } catch (ex) {
      const status = ex?.response?.status;
      if (status === 422) setErr("Some fields could not be updated.");
      else if (status === 404) setErr("You do not have access to edit this horse.");
      else if (status === 400) setErr(ex?.response?.data?.detail || "No changes to save.");
      else setErr(formatApiError(ex));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={onClose}>
      <form className="se-card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="horse-owner-edit-form">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading" style={{ fontSize: 20 }}>Edit my horse</h3>
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose} style={{ padding: 6 }}><X size={14} /></button>
        </div>

        <PhotoUpload
          value={form.photo_url}
          onChange={(url) => setForm({ ...form, photo_url: url || "" })}
          testidPrefix="horse-owner-edit-photo"
        />

        <label className="se-label block mb-1 mt-3">Name *</label>
        <input className="se-input mb-3" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="horse-owner-edit-name" />

        <label className="se-label block mb-1">Nickname</label>
        <input className="se-input mb-3" value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} data-testid="horse-owner-edit-nickname" />

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="se-label block mb-1">Date of birth</label>
            <input type="date" className="se-input" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="horse-owner-edit-dob" />
          </div>
          <div>
            <label className="se-label block mb-1">Discipline</label>
            <select className="se-input" value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} data-testid="horse-owner-edit-discipline">
              <option value="">—</option>
              <option value="show_jumping">Show jumping</option>
              <option value="dressage">Dressage</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="se-label block mb-1">Passport number</label>
            <input className="se-input" value={form.passport_number} onChange={(e) => setForm({ ...form, passport_number: e.target.value })} data-testid="horse-owner-edit-passport" />
          </div>
          <div>
            <label className="se-label block mb-1">Microchip number</label>
            <input className="se-input" value={form.microchip_number} onChange={(e) => setForm({ ...form, microchip_number: e.target.value })} data-testid="horse-owner-edit-microchip" />
          </div>
        </div>

        <label className="se-label block mb-1">Notes</label>
        <textarea className="se-input mb-3" style={{ height: 80, padding: "10px 14px" }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="horse-owner-edit-notes" />

        {/* ENH-018: Vet Contact — owner can maintain their horse's vet contact */}
        <div className="se-divider my-3" />
        <div className="se-label mb-2">Vet Contact</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="se-label block mb-1">Vet name</label>
            <input className="se-input" value={form.vet_contact_name} onChange={(e) => setForm({ ...form, vet_contact_name: e.target.value })} data-testid="horse-owner-edit-vet-contact-name" />
          </div>
          <div>
            <label className="se-label block mb-1">Vet phone</label>
            <input className="se-input" value={form.vet_contact_phone} onChange={(e) => setForm({ ...form, vet_contact_phone: e.target.value })} data-testid="horse-owner-edit-vet-contact-phone" />
          </div>
        </div>
        <label className="se-label block mb-1">Vet notes</label>
        <textarea className="se-input mb-3" style={{ height: 60, padding: "10px 14px" }} value={form.vet_contact_notes} onChange={(e) => setForm({ ...form, vet_contact_notes: e.target.value })} data-testid="horse-owner-edit-vet-contact-notes" />

        {/* ENH-009: calendar colour picker for owner */}
        <div className="se-divider my-3" />
        <div className="se-label mb-2">Calendar colour</div>
        <ColorSwatchPicker
          value={form.calendar_color}
          onChange={(c) => setForm({ ...form, calendar_color: c })}
          testidPrefix="horse-owner-edit-color"
        />

        {err && <div className="text-sm px-3 py-2 rounded-lg mb-3" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="horse-owner-edit-error">{err}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="se-btn" disabled={busy} data-testid="horse-owner-edit-submit">
            <Save size={14} /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="se-label">{label}</div>
    <div className="mt-1">{value || "—"}</div>
  </div>
);

// ENH-009: reusable 20-swatch palette picker + optional "clear" button.
function ColorSwatchPicker({ value, onChange, testidPrefix = "color-swatch" }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-row`}>
      {CALENDAR_PALETTE.map((c) => {
        const active = (value || "").toLowerCase() === c.toLowerCase();
        return (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            aria-label={`Choose ${c}`}
            title={c}
            className="rounded-full"
            style={{
              width: 28, height: 28,
              background: c,
              border: active ? "3px solid #3B2F1E" : "1px solid rgba(0,0,0,0.15)",
              boxShadow: active ? "0 0 0 2px #F5EFE6 inset" : undefined,
            }}
            data-testid={`${testidPrefix}-${c.replace("#", "").toLowerCase()}`}
          />
        );
      })}
      {value && (
        <button type="button" onClick={() => onChange("")} className="text-xs se-link" data-testid={`${testidPrefix}-clear`}>Clear</button>
      )}
    </div>
  );
}

// ENH-018: read-only "Vet Contact" card shown on every horse profile just above
// the care tracker. All roles can view (per spec: groom + driver see it read-
// only — same treatment as any other display card). Empty state renders when
// all three fields are blank.
function VetContactCard({ horse }) {
  const hasAny = horse.vet_contact_name || horse.vet_contact_phone || horse.vet_contact_notes;
  return (
    <div className="se-card" data-testid="vet-contact-card">
      <div className="flex items-center gap-2 mb-3">
        <Stethoscope size={16} style={{ color: "#7C5C3A" }} />
        <h3 className="font-heading" style={{ fontSize: 18 }}>Vet Contact</h3>
      </div>
      {!hasAny && (
        <p className="text-sm" style={{ color: "#8C9E8A" }} data-testid="vet-contact-empty">
          No vet contact set
        </p>
      )}
      {hasAny && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <div className="se-label">Name</div>
            <div className="mt-1" data-testid="vet-contact-name">{horse.vet_contact_name || "—"}</div>
          </div>
          <div>
            <div className="se-label">Phone</div>
            <div className="mt-1">
              {horse.vet_contact_phone ? (
                <a
                  href={`tel:${horse.vet_contact_phone.replace(/[^+\d]/g, "")}`}
                  className="se-link"
                  data-testid="vet-contact-phone"
                >
                  {horse.vet_contact_phone}
                </a>
              ) : "—"}
            </div>
          </div>
          {horse.vet_contact_notes && (
            <div className="md:col-span-2">
              <div className="se-label">Notes</div>
              <p className="mt-1 whitespace-pre-wrap" data-testid="vet-contact-notes">{horse.vet_contact_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
